import * as THREE from "three";

const CYAN = 0x22d3ee;
const VOLT = 0xffe600;

function glowTexture(colorStops) {
    const c = document.createElement("canvas");
    c.width = c.height = 64;
    const g = c.getContext("2d");
    const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    colorStops.forEach(([stop, col]) => grad.addColorStop(stop, col));
    g.fillStyle = grad;
    g.fillRect(0, 0, 64, 64);
    return new THREE.CanvasTexture(c);
}

function makeLabel(text) {
    const c = document.createElement("canvas");
    c.width = 256;
    c.height = 64;
    const g = c.getContext("2d");
    const draw = (txt) => {
        g.clearRect(0, 0, 256, 64);
        g.fillStyle = "rgba(8,12,20,0.88)";
        g.fillRect(0, 0, 256, 64);
        g.strokeStyle = "rgba(34,211,238,0.55)";
        g.lineWidth = 4;
        g.strokeRect(0, 0, 256, 64);
        g.font = "bold 26px monospace";
        g.fillStyle = "#a5f3fc";
        g.textAlign = "center";
        g.textBaseline = "middle";
        g.fillText(txt, 128, 34);
    };
    draw(text);
    const sp = new THREE.Sprite(
        new THREE.SpriteMaterial({
            map: new THREE.CanvasTexture(c),
            transparent: true,
            depthWrite: false,
            toneMapped: false,
        })
    );
    sp.scale.set(1.75, 0.44, 1);
    return { sprite: sp, draw, canvas: c };
}

export class TiScene {
    constructor(canvas, opts = {}) {
        this.canvas = canvas;
        this.onPacket = opts.onPacket || (() => {});
        this.inView = false;
        this.disposed = false;
        this.packets = [];
        this.pulses = [];
        this.ambientTimer = 1.2;
        this.pointer = { x: 0, y: 0, tx: 0, ty: 0 };
        this.pathCache = {};

        this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
        this.renderer.toneMapping = THREE.NoToneMapping;

        this.scene = new THREE.Scene();
        this.scene.fog = new THREE.FogExp2(0x0a0d14, 0.045);

        this.camera = new THREE.PerspectiveCamera(38, 16 / 9, 0.1, 120);
        this.camBase = new THREE.Vector3(8.8, 5.9, 10.6);
        this.camera.position.copy(this.camBase);

        const dir = new THREE.DirectionalLight(0x8ea6d8, 1.5);
        dir.position.set(5, 12, 6);
        this.scene.add(dir, new THREE.AmbientLight(0x1e2c3f, 2.4));

        this.glowCyan = glowTexture([
            [0, "rgba(255,255,255,1)"],
            [0.3, "rgba(34,211,238,0.9)"],
            [1, "rgba(34,211,238,0)"],
        ]);
        this.glowVolt = glowTexture([
            [0, "rgba(255,255,255,1)"],
            [0.3, "rgba(255,230,0,0.9)"],
            [1, "rgba(255,230,0,0)"],
        ]);
        this.packetGeo = new THREE.SphereGeometry(1, 10, 10);
        this.matPacket = new THREE.MeshBasicMaterial({ color: CYAN, transparent: true, opacity: 0.55, toneMapped: false });
        this.matPacketVolt = new THREE.MeshBasicMaterial({ color: VOLT, toneMapped: false });

        this.buildNetwork();

        this._onPointer = (e) => {
            const r = canvas.getBoundingClientRect();
            this.pointer.tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
            this.pointer.ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
        };
        canvas.parentElement.addEventListener("pointermove", this._onPointer);

        this.clock = new THREE.Clock();
        this._loop = this.loop.bind(this);
        this._loop();
    }

    buildNetwork() {
        const g = new THREE.Group();
        const faceMat = new THREE.MeshStandardMaterial({ color: 0x0d1524, roughness: 0.8, metalness: 0.25 });
        const edgeMat = new THREE.LineBasicMaterial({ color: 0x2f5d78, transparent: true, opacity: 0.95 });
        this.ledMats = [];

        const box = (w, h, d, x, y, z) => {
            const geo = new THREE.BoxGeometry(w, h, d);
            const m = new THREE.Mesh(geo, faceMat);
            m.position.set(x, y + h / 2, z);
            const e = new THREE.LineSegments(new THREE.EdgesGeometry(geo), edgeMat);
            e.position.copy(m.position);
            g.add(m, e);
            return m;
        };

        // internet cloud
        const cloud = new THREE.Group();
        cloud.position.set(0, 6.7, -1.5);
        [[0, 0, 0], [0.75, 0.25, 0], [-0.75, 0.15, 0], [0.3, 0.55, 0.1], [-0.35, 0.5, -0.1]].forEach((p, i) => {
            const s = new THREE.Mesh(
                new THREE.IcosahedronGeometry(i < 3 ? 0.85 : 0.6, 1),
                new THREE.MeshBasicMaterial({ color: CYAN, wireframe: true, transparent: true, opacity: 0.5, toneMapped: false })
            );
            s.position.set(...p);
            cloud.add(s);
        });
        g.add(cloud);

        // router
        box(2.0, 0.5, 1.15, 0, 4.35, 0);
        [-0.55, 0.55].forEach((x) => {
            const ant = new THREE.Mesh(
                new THREE.CylinderGeometry(0.02, 0.02, 0.7, 6),
                new THREE.MeshBasicMaterial({ color: CYAN, transparent: true, opacity: 0.8, toneMapped: false })
            );
            ant.position.set(x, 5.15, 0);
            ant.rotation.z = x > 0 ? -0.3 : 0.3;
            g.add(ant);
        });

        // server rack
        const srv = box(1.5, 3.0, 1.2, -3.4, 0, 0.4);
        for (let i = 0; i < 5; i++) {
            const led = new THREE.Mesh(
                new THREE.PlaneGeometry(0.09, 0.09),
                new THREE.MeshBasicMaterial({ color: i === 0 ? VOLT : CYAN, transparent: true, opacity: 0.9, toneMapped: false, side: THREE.DoubleSide })
            );
            led.position.set(-3.75, 2.72 - i * 0.5, 1.02);
            g.add(led);
            this.ledMats.push(led.material);
        }
        srv.userData.leds = true;

        // workstations
        [2.35, 3.55].forEach((x, i) => {
            const mon = box(0.95, 0.62, 0.07, x, 1.15, -0.6);
            const stand = new THREE.Mesh(
                new THREE.CylinderGeometry(0.03, 0.03, 0.5, 6),
                new THREE.MeshBasicMaterial({ color: 0x2f5d78, toneMapped: false })
            );
            stand.position.set(x + 0.475, 0.9, -0.6);
            const base = box(0.5, 0.05, 0.35, x, 0.15, -0.6);
            g.add(stand);
            const screen = new THREE.Mesh(
                new THREE.PlaneGeometry(0.8, 0.48),
                new THREE.MeshBasicMaterial({ color: 0x155e75, transparent: true, opacity: 0.95, toneMapped: false })
            );
            screen.position.set(x + 0.475, 1.46 + 0.31, -0.555);
            g.add(screen);
        });

        // wi-fi AP pole
        const apPole = new THREE.Mesh(
            new THREE.CylinderGeometry(0.03, 0.045, 2.5, 8),
            new THREE.MeshStandardMaterial({ color: 0x132030, roughness: 0.7 })
        );
        apPole.position.set(0.6, 1.25, 2.7);
        const apTip = new THREE.Mesh(
            new THREE.SphereGeometry(0.14, 12, 12),
            new THREE.MeshBasicMaterial({ color: CYAN, toneMapped: false })
        );
        apTip.position.set(0.6, 2.62, 2.7);
        g.add(apPole, apTip);
        this.wifiRings = [0, 1].map((i) => {
            const ring = new THREE.Mesh(
                new THREE.TorusGeometry(0.32, 0.02, 8, 40),
                new THREE.MeshBasicMaterial({ color: CYAN, transparent: true, opacity: 0.6, toneMapped: false })
            );
            ring.rotation.x = -Math.PI / 2;
            ring.position.set(0.6, 2.62, 2.7);
            ring.userData.phase = i * 0.5;
            g.add(ring);
            return ring;
        });

        // cctv pole + camera
        const cPole = new THREE.Mesh(
            new THREE.CylinderGeometry(0.03, 0.045, 2.7, 8),
            new THREE.MeshStandardMaterial({ color: 0x132030, roughness: 0.7 })
        );
        cPole.position.set(-1.8, 1.35, 2.9);
        const camBody = box(0.22, 0.22, 0.62, -1.91, 2.7, 2.59);
        camBody.rotation.y = -0.5;
        g.add(cPole);
        const lens = new THREE.Mesh(
            new THREE.CircleGeometry(0.07, 16),
            new THREE.MeshBasicMaterial({ color: VOLT, toneMapped: false })
        );
        lens.position.set(-1.62, 2.9, 2.38);
        lens.rotation.y = -0.5 + Math.PI;
        g.add(lens);

        // ground
        const ground = new THREE.Mesh(
            new THREE.CircleGeometry(20, 48),
            new THREE.MeshBasicMaterial({ color: 0x0b101d })
        );
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -0.02;
        g.add(ground);
        const grid = new THREE.GridHelper(38, 38, 0x1e3348, 0x12202e);
        grid.position.y = 0.01;
        g.add(grid);

        // connections
        const routerTop = new THREE.Vector3(0, 4.75, 0);
        const cloudBottom = new THREE.Vector3(0, 6.0, -1.5);
        const nodeTops = {
            server: new THREE.Vector3(-3.4, 3.15, 0.4),
            stations: new THREE.Vector3(2.95, 1.95, -0.6),
            wifi: new THREE.Vector3(0.6, 2.7, 2.7),
            cctv: new THREE.Vector3(-1.8, 2.85, 2.9),
        };
        const link = (a, b, bright) => {
            const mid = a.clone().add(b).multiplyScalar(0.5);
            mid.y -= 0.7;
            const curve = new THREE.CatmullRomCurve3([a, mid, b]);
            const tube = new THREE.Mesh(
                new THREE.TubeGeometry(curve, 40, 0.016, 5, false),
                new THREE.MeshBasicMaterial({ color: CYAN, transparent: true, opacity: bright ? 0.5 : 0.3, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false })
            );
            g.add(tube);
            return curve;
        };
        const c1 = link(cloudBottom, routerTop, true);
        this.paths = {};
        Object.entries(nodeTops).forEach(([key, top]) => {
            const c2 = link(routerTop, top);
            const pts = c1.getPoints(40).concat(c2.getPoints(40).slice(1));
            this.paths[key] = new THREE.CatmullRomCurve3(pts);
        });

        // labels
        this.labelSlots = [
            { pos: new THREE.Vector3(0, 7.9, -1.5) },
            { pos: new THREE.Vector3(0, 5.75, 0) },
            { pos: new THREE.Vector3(-3.4, 3.85, 0.4) },
            { pos: new THREE.Vector3(2.95, 2.5, -0.6) },
            { pos: new THREE.Vector3(0.6, 3.35, 2.7) },
            { pos: new THREE.Vector3(-1.8, 3.5, 2.9) },
        ];
        this.labels = [];
        this.labelSlots.forEach((slot) => {
            const obj = makeLabel("");
            obj.sprite.position.copy(slot.pos);
            g.add(obj.sprite);
            this.labels.push(obj);
        });

        this.nodeTops = nodeTops;
        this.scene.add(g);
    }

    setLabels(labels) {
        if (!this.labels || labels.length < this.labels.length) return;
        this.labels.forEach((l, i) => l.draw(labels[i]));
        this.labels.forEach((l) => (l.sprite.material.map.needsUpdate = true));
    }

    pulse(destKey, volt) {
        const top = this.nodeTops[destKey];
        const ring = new THREE.Mesh(
            new THREE.TorusGeometry(0.34, 0.03, 8, 40),
            new THREE.MeshBasicMaterial({ color: volt ? VOLT : CYAN, transparent: true, opacity: 0.9, toneMapped: false })
        );
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(top.x, top.y + 0.05, top.z);
        this.scene.add(ring);
        this.pulses.push({ ring, t: 0 });
    }

    sendPacket(destKey, volt = false) {
        const curve = this.paths[destKey];
        if (!curve) return;
        const mesh = new THREE.Mesh(this.packetGeo, volt ? this.matPacketVolt : this.matPacket);
        mesh.scale.setScalar(volt ? 0.075 : 0.05);
        const halo = new THREE.Sprite(
            new THREE.SpriteMaterial({
                map: volt ? this.glowVolt : this.glowCyan,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                toneMapped: false,
                opacity: volt ? 0.95 : 0.45,
            })
        );
        this.scene.add(mesh, halo);
        this.packets.push({
            curve,
            mesh,
            halo,
            t: 0,
            speed: volt ? 1.25 : 0.55 + Math.random() * 0.35,
            dest: destKey,
            volt,
        });
    }

    resize(w, h) {
        if (!w || !h) return;
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(w, h, false);
    }

    loop() {
        if (this.disposed) return;
        this._raf = requestAnimationFrame(this._loop);
        const dt = Math.min(this.clock.getDelta(), 0.05);
        const time = this.clock.elapsedTime;

        // server LEDs
        this.ledMats.forEach((m, i) => {
            m.opacity = Math.sin(time * 3 + i * 1.4) > 0 ? 0.95 : 0.15;
        });

        // wifi rings
        this.wifiRings.forEach((ring) => {
            const p = (time * 0.7 + ring.userData.phase) % 1;
            ring.scale.setScalar(1 + p * 2.6);
            ring.material.opacity = 0.55 * (1 - p);
        });

        // pulses
        for (let i = this.pulses.length - 1; i >= 0; i--) {
            const p = this.pulses[i];
            p.t += dt * 1.4;
            const q = Math.min(p.t, 1);
            p.ring.scale.setScalar(1 + q * 3.2);
            p.ring.material.opacity = 0.9 * (1 - q);
            if (p.t >= 1) {
                this.scene.remove(p.ring);
                p.ring.geometry.dispose();
                p.ring.material.dispose();
                this.pulses.splice(i, 1);
            }
        }

        // packets
        for (let i = this.packets.length - 1; i >= 0; i--) {
            const p = this.packets[i];
            p.t += p.speed * dt;
            const pos = p.curve.getPointAt(Math.min(p.t, 1));
            p.mesh.position.copy(pos);
            p.halo.position.copy(pos);
            if (p.t >= 1) {
                this.scene.remove(p.mesh, p.halo);
                p.halo.material.dispose();
                if (p.volt) {
                    this.pulse(p.dest, true);
                    this.onPacket({
                        latency: (6 + Math.random() * 16).toFixed(1),
                        dest: p.dest,
                    });
                }
                this.packets.splice(i, 1);
            }
        }

        // ambient traffic
        if (this.inView && !document.hidden) {
            this.ambientTimer -= dt;
            if (this.ambientTimer <= 0) {
                const keys = Object.keys(this.paths);
                this.sendPacket(keys[Math.floor(Math.random() * keys.length)], false);
                this.ambientTimer = 1.4 + Math.random() * 1.6;
            }
        }

        // camera parallax
        this.pointer.x += (this.pointer.tx - this.pointer.x) * Math.min(1, dt * 3);
        this.pointer.y += (this.pointer.ty - this.pointer.y) * Math.min(1, dt * 3);
        this.camera.position.x = this.camBase.x + this.pointer.x * 1.0;
        this.camera.position.y = this.camBase.y + this.pointer.y * 0.5;
        this.camera.lookAt(0, 3.1, 0);

        this.renderer.render(this.scene, this.camera);
    }

    dispose() {
        this.disposed = true;
        cancelAnimationFrame(this._raf);
        this.canvas.parentElement.removeEventListener("pointermove", this._onPointer);
        this.scene.traverse((o) => {
            if (o.geometry) o.geometry.dispose();
            if (o.material) {
                if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
                else o.material.dispose();
            }
        });
        this.renderer.dispose();
    }
}
